import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../web/components-v2/Button';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { Modal } from '../../../web/components/Modal';
import {
	useExecuteBillingCycleChangeMutation,
	usePreviewBillingCycleChangeMutation,
	useProformaInvoiceUrlMutation,
	useSubscriptionStateQuery,
} from '../../../queries/Admin/UpgradeWizard';
import type { BillingCycleChangePreviewResult, BillingCycleChangeResult } from '../tools/UpgradeWizard/types';
import { styleSheet } from './styles';

type Step = 'select' | 'preview' | 'success';

interface BillingCycleOption {
	label: string;
	months: 3 | 12;
	description: string;
}

const CYCLE_OPTIONS: BillingCycleOption[] = [
	{ label: 'Quarterly', months: 3, description: 'Billed every 3 months' },
	{ label: 'Annual', months: 12, description: 'Billed every 12 months' },
];

interface BillingCycleChangeModalProps {
	isOpen: boolean;
	accountId: string;
	billingReferenceId?: number | null;
	onClose: () => void;
}

export function BillingCycleChangeModal({
	isOpen,
	accountId,
	billingReferenceId,
	onClose,
}: BillingCycleChangeModalProps) {
	const [step, setStep] = React.useState<Step>('select');
	const [selectedCycle, setSelectedCycle] = React.useState<3 | 12 | null>(null);
	const [previewResult, setPreviewResult] = React.useState<BillingCycleChangePreviewResult | null>(null);
	const [executeResult, setExecuteResult] = React.useState<BillingCycleChangeResult | null>(null);
	const subscriptionQuery = useSubscriptionStateQuery({
		accountId,
		enabled: isOpen && !!accountId && !!billingReferenceId,
	});
	const previewMutation = usePreviewBillingCycleChangeMutation();
	const executeMutation = useExecuteBillingCycleChangeMutation();
	const proformaInvoiceMutation = useProformaInvoiceUrlMutation();

	const handleClose = () => {
		setStep('select');
		setSelectedCycle(null);
		setPreviewResult(null);
		setExecuteResult(null);
		previewMutation.reset();
		executeMutation.reset();
		onClose();
	};

	const subscriptionState = subscriptionQuery.data;
	const currentCycleMonths = subscriptionState?.billingCycleMonths;
	const availableOptions = CYCLE_OPTIONS.filter(opt => opt.months !== currentCycleMonths);

	const handlePreview = () => {
		if (!selectedCycle) return;
		previewMutation.mutate(
			{ accountId, newBillingCycle: selectedCycle },
			{
				onSuccess: result => {
					setPreviewResult(result);
					setStep('preview');
				},
			}
		);
	};

	const handleExecute = () => {
		if (!selectedCycle) return;
		executeMutation.mutate(
			{ accountId, newBillingCycle: selectedCycle },
			{
				onSuccess: result => {
					setExecuteResult(result);
					setStep('success');

					// Fetch proforma invoice URL
					const customerId = billingReferenceId;
					if (customerId) {
						proformaInvoiceMutation.mutate(customerId);
					}
				},
				onError: () => {
					setExecuteResult({
						subscriptionId: 0,
						success: false,
						errorMessage: 'An unexpected error occurred.',
						componentSwapResults: [],
					});
					setStep('success');
				},
			}
		);
	};

	const renderSelectStep = () => {
		if (subscriptionQuery.isLoading) {
			return (
				<div className={css(styleSheet.container)}>
					<LoadingSpinner type='large' />
				</div>
			);
		}

		if (subscriptionQuery.isError) {
			return (
				<div className={css(styleSheet.container)}>
					<div className={css(styleSheet.errorMessage)}>Failed to load subscription state. Please try again.</div>
				</div>
			);
		}

		const isEligible = subscriptionState?.isEligibleForBillingCycleChange ?? false;
		const blockers = subscriptionState?.billingCycleChangeBlockers ?? [];

		return (
			<div className={css(styleSheet.container)}>
				<h2 className={css(styleSheet.title)}>Change Billing Cycle</h2>
				<p className={css(styleSheet.subtitle)}>Select a new billing cycle for this account</p>

				<div className={css(styleSheet.currentCycleCard)}>
					<div className={css(styleSheet.currentCycleLabel)}>Current Billing Cycle</div>
					<div className={css(styleSheet.currentCycleValue)}>{subscriptionState?.billingCycleLabel ?? 'Unknown'}</div>
				</div>

				{!isEligible && blockers.length > 0 && (
					<div className={css(styleSheet.blockerBox)}>
						<div className={css(styleSheet.blockerTitle)}>This account is not eligible for a billing cycle change:</div>
						<ul className={css(styleSheet.blockerList)}>
							{blockers.map((blocker, i) => (
								<li key={i}>{blocker}</li>
							))}
						</ul>
					</div>
				)}

				{!isEligible && blockers.length === 0 && (
					<div className={css(styleSheet.blockerBox)}>
						<div className={css(styleSheet.blockerTitle)}>This account is not eligible for a billing cycle change.</div>
					</div>
				)}

				{isEligible &&
					availableOptions.map(option => {
						const isSelected = selectedCycle === option.months;
						return (
							<div
								key={option.months}
								role='button'
								tabIndex={0}
								className={css(styleSheet.cycleOption, isSelected && styleSheet.cycleOptionSelected)}
								onClick={() => setSelectedCycle(option.months)}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.preventDefault();
										setSelectedCycle(option.months);
									}
								}}
							>
								<div className={css(styleSheet.cycleOptionTitle)}>
									<div className={css(styleSheet.radioIndicator, isSelected && styleSheet.radioIndicatorSelected)}>
										{isSelected && <div className={css(styleSheet.radioIndicatorDot)} />}
									</div>
									{option.label}
								</div>
								<div className={css(styleSheet.cycleOptionDescription)}>{option.description}</div>
							</div>
						);
					})}

				<div className={css(styleSheet.buttonRow)}>
					<Button variant='outline' onClick={handleClose}>
						Cancel
					</Button>
					<Button
						variant='primary'
						onClick={handlePreview}
						disabled={!isEligible || !selectedCycle || previewMutation.isPending}
					>
						{previewMutation.isPending ? 'Loading...' : 'Preview Change'}
					</Button>
				</div>

				{previewMutation.isError && (
					<div className={css(styleSheet.errorMessage)}>
						{previewMutation.error instanceof Error
							? previewMutation.error.message
							: 'Failed to preview billing cycle change'}
					</div>
				)}
			</div>
		);
	};

	const renderPreviewStep = () => {
		if (!previewResult) return null;

		return (
			<div className={css(styleSheet.container)}>
				<h2 className={css(styleSheet.title)}>Confirm Billing Cycle Change</h2>

				<div className={css(styleSheet.previewMessage)}>{previewResult.scheduledChange}</div>

				<div className={css(styleSheet.previewDetail)}>
					<span className={css(styleSheet.previewDetailLabel)}>New Billing Cycle</span>
					<span className={css(styleSheet.previewDetailValue)}>{previewResult.targetBillingCycleLabel}</span>
				</div>

				<p className={css(styleSheet.noteText)}>
					This change will take effect at the next renewal. The billing cycle shown on the account will not update until
					then.
				</p>

				<div className={css(styleSheet.buttonRow)}>
					<Button variant='outline' onClick={() => setStep('select')}>
						Back
					</Button>
					<Button variant='primary' onClick={handleExecute} disabled={executeMutation.isPending}>
						{executeMutation.isPending ? 'Processing...' : 'Confirm Change'}
					</Button>
				</div>

				{executeMutation.isError && (
					<div className={css(styleSheet.errorMessage)}>
						{executeMutation.error instanceof Error
							? executeMutation.error.message
							: 'Failed to execute billing cycle change'}
					</div>
				)}
			</div>
		);
	};

	const renderSuccessStep = () => {
		if (!executeResult) return null;

		if (!executeResult.success) {
			return (
				<div className={css(styleSheet.container)}>
					<div className={css(styleSheet.successContainer)}>
						<h2 className={css(styleSheet.title)}>Billing Cycle Change Failed</h2>
						<div className={css(styleSheet.errorMessage)}>
							{executeResult.errorMessage || 'The billing cycle change could not be completed. Please try again.'}
						</div>
						<Button variant='primary' onClick={handleClose}>
							Close
						</Button>
					</div>
				</div>
			);
		}

		const failedSwaps = executeResult.componentSwapResults.filter(r => !r.success);

		return (
			<div className={css(styleSheet.container)}>
				<div className={css(styleSheet.successContainer)}>
					<h2 className={css(styleSheet.successTitle)}>Billing Cycle Change Scheduled</h2>
					<p className={css(styleSheet.successMessage)}>
						{executeResult.scheduledProductChange || 'The billing cycle change has been scheduled successfully.'}
					</p>

					<p className={css(styleSheet.noteText)}>
						The billing cycle on the account details page will not update until the renewal date. To confirm the change
						was scheduled, you can preview the next invoice below.
					</p>

					{failedSwaps.length > 0 && (
						<div>
							{failedSwaps.map((swap, i) => (
								<p key={i} className={css(styleSheet.warningItem)}>
									Warning: Failed to swap component {swap.oldHandle}
									{swap.errorMessage ? ` — ${swap.errorMessage}` : ''}
								</p>
							))}
						</div>
					)}

					{proformaInvoiceMutation.data && (
						<p className={css(styleSheet.successMessage)}>
							<a
								href={proformaInvoiceMutation.data}
								target='_blank'
								rel='noopener noreferrer'
								className={css(styleSheet.invoiceLink)}
							>
								Preview Next Invoice →
							</a>
						</p>
					)}
					{proformaInvoiceMutation.isPending && (
						<p className={css(styleSheet.loadingText)}>Loading invoice preview link...</p>
					)}
					{proformaInvoiceMutation.isError && (
						<p className={css(styleSheet.errorText)}>Could not load invoice preview link.</p>
					)}

					<Button variant='primary' onClick={handleClose}>
						Close
					</Button>
				</div>
			</div>
		);
	};

	return (
		<Modal isOpen={isOpen} onRequestClose={handleClose} useDefaultHeader={true}>
			{step === 'select' && renderSelectStep()}
			{step === 'preview' && renderPreviewStep()}
			{step === 'success' && renderSuccessStep()}
		</Modal>
	);
}
